import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { debounce } from 'lodash';
import { Card, Container, Input } from 'reactstrap';
import { useBoolean } from 'usehooks-ts';
import { useDispatch, useSelector } from 'react-redux';

import Breadcrumb from 'components/App/Breadcrumb';
import Button from 'components/App/Button';
import DataTableAccommodationAccount from 'components/App/DataTableAccommodationAccount';
import Meta from 'components/App/Meta';
import Pagination from 'components/Pagination';
import Loading from 'components/App/Loading';
import ModalAddAccommodationAccount from 'components/Modals/ModalAddAccommodationAccount';
import ModalEditAccommodationAccount from 'components/Modals/ModalEditAccommodationAccount';
import ModalAdminConfirm from 'components/Modals/ModalAdminConfirm';

import { getParamsFormObject, showSuccess, apiErrorHandler } from 'utils';
import {
  actionGetExtranetAdministratorsList,
  actionGetExtranetAdministratorsPlaceManager,
  actionEditExtranetAdministrator,
  actionDeleteExtranetAdministrator,
  actionDeleteAdministrator,
  actionAddExtranetAdministrator,
} from 'store/administrator/actions';

import AddIcon from 'assets/images/add-white.svg';
import SearchIcon from 'assets/images/search-icon.svg';

import styles from './ManagerAccommodationAccount.module.scss';

const PAGE_TITLE = 'Tài khoản chỗ nghỉ';
const DEFAULT_LIMIT = 10;

const convertPermissionToAccessRights = (permission) => {
  if (!permission) return '';

  const list = [];
  if (permission.price) list.push('Giá và tình trạng phòng trống');
  if (permission.promotion) list.push('Khuyến mãi');

  if (permission.booking) {
    if (permission.booking.mainInformation) {
      list.push('Đặt phòng');
    }
    if (permission.booking.paymentInformation) {
      list.push('Xem thông tin thẻ tín dụng');
    }
    if (permission.booking.contactInformation) {
      list.push('Xem thông tin liên hệ của khách');
    }
  }

  if (permission.place) list.push('Chỗ nghỉ');
  if (permission.review) list.push('Đánh giá');
  if (permission.finance) list.push('Tài chính');
  if (permission.analytic) list.push('Phân tích');

  return list.join(', ');
};

const breadcrumbList = [
  {
    name: 'Quản lý tài khoản',
    isActive: false,
    link: '',
  },
  {
    name: PAGE_TITLE,
    isActive: true,
    link: '',
  },
];

function ManagerAccommodationAccount() {
  const history = useHistory();
  const tableRef = React.useRef(null);
  const dispatch = useDispatch();

  const extranetAdministratorsList = useSelector((state) =>
    state.Administrator.extranetAdministratorsList);
  const extranetAdministratorsTotal = useSelector((state) =>
    state.Administrator.extranetAdministratorsTotal);
  const isLoadingGetExtranetAdministratorsList = useSelector((state) =>
    state.Administrator.isLoadingGetExtranetAdministratorsList);
  const isLoadingDeleteExtranetAdministrator = useSelector((state) =>
    state.Administrator.isLoadingDeleteExtranetAdministrator);
  const isLoadingDeleteAdministrator = useSelector((state) =>
    state.Administrator.isLoadingDeleteAdministrator);

  const [isOpenModalDeleteAccount, setIsOpenModalDeleteAccount] = useState(false);
  const [selectedAccountIdForDelete, setSelectedAccountIdForDelete] = useState(null);
  const [selectedPlaceIdForDelete, setSelectedPlaceIdForDelete] = useState(null);

  const searchQueries = useMemo(() => {
    const params = new URLSearchParams(history.location.search);

    return {
      page: Number(params.get('page')) || 1,
      q: params.get('q') || '',
    };
  }, [history.location.search]);

  useEffect(() => {
    dispatch(
      actionGetExtranetAdministratorsList({
        page: searchQueries.page,
        limit: DEFAULT_LIMIT,
        q: searchQueries.q,
      }),
    );
  }, [dispatch, searchQueries.page, searchQueries.q]);

  const mappedAccountList = useMemo(() => (extranetAdministratorsList || []).map((item) => {
    const isSingle = (item.accountType || 'SINGLE') === 'SINGLE';
    let parentAccessRights = item.accessRights || '';
    if (isSingle) {
      parentAccessRights = 'Quản trị viên có toàn quyền truy cập vào extranet của chỗ nghỉ';
    } else if (item.placePermission?.permission) {
      parentAccessRights = convertPermissionToAccessRights(item.placePermission.permission);
    }

    return {
      ...item,
      places: item.place?.name || item.places || '',
      accountType: item.accountType || 'SINGLE',
      accessRights: parentAccessRights,
      children: (item.children || []).map((child) => {
        const childAccessRights = child.placePermission?.permission
          ? convertPermissionToAccessRights(child.placePermission.permission)
          : child.accessRights || '';
        return {
          ...child,
          places: child.place?.name || child.places || '',
          accountType: 'SUB',
          accessRights: childAccessRights,
        };
      }),
    };
  }), [extranetAdministratorsList]);

  const pageCount = useMemo(
    () => Math.ceil((extranetAdministratorsTotal || 0) / DEFAULT_LIMIT) || 0,
    [extranetAdministratorsTotal],
  );

  const onChangePage = useCallback((selected) => {
    history.push({
      search: getParamsFormObject({
        q: searchQueries.q,
        page: selected.selected + 1,
      }),
    });
    tableRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history, searchQueries.q]);

  const debouncedUpdateSearchQuery = useMemo(
    () => debounce((keyword) => {
      history.push({
        search: getParamsFormObject({
          q: keyword,
          page: 1,
        }),
      });
    }, 500),
    [history],
  );

  const onSearch = useCallback((event) => {
    debouncedUpdateSearchQuery(event.target.value);
  }, [debouncedUpdateSearchQuery]);

  const {
    value: isOpenModalAddAccount,
    setTrue: onOpenModalAddAccount,
    toggle: onToggleModalAddAccount,
  } = useBoolean(false);

  const {
    value: isOpenModalEditAccount,
    setTrue: onOpenModalEditAccount,
    toggle: onToggleModalEditAccount,
  } = useBoolean(false);

  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [selectedPlaceId, setSelectedPlaceId] = useState(null);
  const [selectedPlaceIdForAdd, setSelectedPlaceIdForAdd] = useState(null);
  const [selectedPlaceNameForAdd, setSelectedPlaceNameForAdd] = useState('');

  const selectedAccountDetails = useMemo(() => {
    if (!selectedAccountId) return null;

    if (selectedPlaceId) {
      const parent = (mappedAccountList || []).find(
        (item) =>
          item._id === selectedAccountId &&
          item.place?._id === selectedPlaceId,
      );
      if (parent) return parent;

      const parentForPlace = (mappedAccountList || []).find(
        (item) => item.place?._id === selectedPlaceId,
      );
      if (parentForPlace) {
        const child = (parentForPlace.children || []).find(
          (c) => c._id === selectedAccountId,
        );
        if (child) {
          return {
            ...child,
            place: child.place || parentForPlace.place,
          };
        }
      }
    }

    const foundParent = (mappedAccountList || []).find(
      (item) => item._id === selectedAccountId,
    );
    if (foundParent) return foundParent;

    const foundChild = (mappedAccountList || [])
      .map((parent) =>
        (parent.children || []).find(
          (child) => child._id === selectedAccountId,
        ),
      )
      .find(Boolean);

    return foundChild || null;
  }, [selectedAccountId, selectedPlaceId, mappedAccountList]);

  const onAddAccount = useCallback(() => {
    setSelectedPlaceIdForAdd(null);
    setSelectedPlaceNameForAdd('');
    onOpenModalAddAccount();
  }, [onOpenModalAddAccount]);

  const onSubmitAddAccount = useCallback((values, { setFieldError, setFieldTouched }) => {
    dispatch(
      actionAddExtranetAdministrator({
        result: {
          email: values.email,
          fullName: values.fullName,
          password: values.password || undefined,
          phoneNumber: values.phoneNumber,
          placeToApply: values.places,
          username: values.username,
          type: selectedPlaceIdForAdd ? 'MANAGER' : 'OWNER',
        },
        callBack: () => {
          showSuccess('Tạo tài khoản phụ thành công');
          onToggleModalAddAccount();
          dispatch(
            actionGetExtranetAdministratorsList({
              page: searchQueries.page,
              limit: DEFAULT_LIMIT,
              q: searchQueries.q,
            }),
          );

          if (selectedPlaceIdForAdd) {
            const parentItem = (extranetAdministratorsList || []).find(
              (item) => item.place?._id === selectedPlaceIdForAdd,
            );
            if (parentItem) {
              dispatch(
                actionGetExtranetAdministratorsPlaceManager({
                  place: selectedPlaceIdForAdd,
                  adminId: parentItem._id,
                  rowKey: `${parentItem._id}_${selectedPlaceIdForAdd}`,
                }),
              );
            }
          }
        },
        onError: (error) => {
          const errorData = error?.response?.data;

          if (errorData?.key === 'USERNAME_ALREADY_EXISTS' || errorData?.code === 40913) {
            setFieldError('username', errorData.message || 'Tên đăng nhập đã tồn tại');
            setFieldTouched('username', true, false);
            return;
          }

          apiErrorHandler(error);
        },
      }),
    );
  }, [
    dispatch,
    onToggleModalAddAccount,
    searchQueries.page,
    searchQueries.q,
    selectedPlaceIdForAdd,
    extranetAdministratorsList,
  ]);

  const onAddSubAccount = useCallback((id, placeId, placeName) => {
    setSelectedPlaceIdForAdd(placeId);
    setSelectedPlaceNameForAdd(placeName || '');
    onOpenModalAddAccount();
  }, [onOpenModalAddAccount]);

  const onEditAccount = useCallback((id, placeId) => {
    setSelectedAccountId(id);
    setSelectedPlaceId(placeId);
    onOpenModalEditAccount();
  }, [onOpenModalEditAccount]);

  const onExpandRow = useCallback((item, rowKey) => {
    if (item.place?._id) {
      dispatch(
        actionGetExtranetAdministratorsPlaceManager({
          place: item.place._id,
          adminId: item._id,
          rowKey: rowKey || `${item._id}_${item.place._id}`,
        }),
      );
    }
  }, [dispatch]);

  const onConfirmDeleteAccount = useCallback(() => {
    const callBackSuccess = () => {
      setIsOpenModalDeleteAccount(false);
      dispatch(
        actionGetExtranetAdministratorsList({
          page: searchQueries.page,
          limit: DEFAULT_LIMIT,
          q: searchQueries.q,
        }),
      );

      const parentItem = (extranetAdministratorsList || []).find((parent) =>
        (parent.children || []).some((child) => child._id === selectedAccountIdForDelete),
      );
      if (parentItem && parentItem.place?._id) {
        dispatch(
          actionGetExtranetAdministratorsPlaceManager({
            place: parentItem.place._id,
            adminId: parentItem._id,
            rowKey: `${parentItem._id}_${parentItem.place._id}`,
          }),
        );
      }
    };

    if (selectedPlaceIdForDelete) {
      dispatch(
        actionDeleteExtranetAdministrator({
          id: selectedAccountIdForDelete,
          place: selectedPlaceIdForDelete,
          callBack: callBackSuccess,
        }),
      );
    } else {
      dispatch(
        actionDeleteAdministrator({
          adminId: selectedAccountIdForDelete,
          callBack: callBackSuccess,
        }),
      );
    }
  }, [
    dispatch,
    searchQueries.page,
    searchQueries.q,
    extranetAdministratorsList,
    selectedAccountIdForDelete,
    selectedPlaceIdForDelete,
  ]);

  const onDeleteAccount = useCallback((id, placeId) => {
    setSelectedAccountIdForDelete(id);
    setSelectedPlaceIdForDelete(placeId);
    setIsOpenModalDeleteAccount(true);
  }, []);

  useEffect(
    () => () => debouncedUpdateSearchQuery.cancel(),
    [debouncedUpdateSearchQuery],
  );

  return (
    <div className={`page-content ${styles.Page}`}>
      <Meta name={PAGE_TITLE} />

      <Container fluid>
        <Breadcrumb
          pageTitle="TÀI KHOẢN CHỖ NGHỈ"
          breadcrumbList={breadcrumbList}
        />

        <Loading loading={isLoadingGetExtranetAdministratorsList} loaderOnPage />

        <Card className="Section">
          <div className="Section__Search">
            <div className="Section__Search__Container">
              <Input
                placeholder="Tìm theo tên, số điện thoại, địa chỉ email"
                className="Section__Search__Input"
                defaultValue={searchQueries.q}
                onChange={onSearch}
              />

              <img
                src={SearchIcon}
                alt="search"
                className="Section__Search__Icon__Search"
              />
            </div>

            <div className="Button__Add__Wrapper">
              <Button
                classes="Button__Add"
                fontSize="13px"
                onClick={onAddAccount}
              >
                <img src={AddIcon} alt="add" />
                Thêm tài khoản
              </Button>
            </div>
          </div>

          <div ref={tableRef}>
            <DataTableAccommodationAccount
              data={mappedAccountList}
              onAddSubAccount={onAddSubAccount}
              onEdit={onEditAccount}
              onDelete={onDeleteAccount}
              onExpand={onExpandRow}
            />
          </div>
        </Card>

        {mappedAccountList.length > 0 && pageCount > 1 && (
          <Pagination
            page={searchQueries.page}
            pageCount={pageCount}
            onChangePage={onChangePage}
          />
        )}
      </Container>

      <ModalAddAccommodationAccount
        isOpen={isOpenModalAddAccount}
        onToggleModal={onToggleModalAddAccount}
        onSubmit={onSubmitAddAccount}
        defaultPlaceId={selectedPlaceIdForAdd}
        defaultPlaceName={selectedPlaceNameForAdd}
      />

      <ModalEditAccommodationAccount
        isOpen={isOpenModalEditAccount}
        onToggleModal={onToggleModalEditAccount}
        administratorsDetail={selectedAccountDetails || {}}
        placeId={selectedPlaceId}
        actionEditAdministrator={(payload) => {
          dispatch(
            actionEditExtranetAdministrator({
              id: selectedAccountId,
              place: selectedPlaceId || undefined,
              result: payload.result,
              callBack: payload.callBack,
            }),
          );
        }}
        actionGetAdministratorsList={() => {
          dispatch(
            actionGetExtranetAdministratorsList({
              page: searchQueries.page,
              limit: DEFAULT_LIMIT,
              q: searchQueries.q,
            }),
          );

          const parentItem = (extranetAdministratorsList || []).find((parent) =>
            (parent.children || []).some((child) => child._id === selectedAccountId),
          );
          if (parentItem && parentItem.place?._id) {
            dispatch(
              actionGetExtranetAdministratorsPlaceManager({
                place: parentItem.place._id,
                adminId: parentItem._id,
                rowKey: `${parentItem._id}_${parentItem.place._id}`,
              }),
            );
          }
        }}
      />

      <ModalAdminConfirm
        isOpen={isOpenModalDeleteAccount}
        onCloseClick={() => setIsOpenModalDeleteAccount(false)}
        onAcceptConfirm={onConfirmDeleteAccount}
        isLoadingBtn={isLoadingDeleteExtranetAdministrator || isLoadingDeleteAdministrator}
        notification={{
          message: 'Bạn có chắc chắn muốn xóa tài khoản này?',
          description: 'Hành động này không thể hoàn tác.',
        }}
        btnConfirmName="Xác nhận"
        btnCloseName="Hủy"
      />
    </div>
  );
}

export default ManagerAccommodationAccount;
